'use client';

import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLocalStorageState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { ProductLineEmpty } from '@/app/components/icons/ProductLineEmpty';
import { ProductLineEmptyFolder } from '@/app/components/icons/ProductLineEmptyFolder';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import ToTop from '@/app/components/shared/ToTop';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { useDevice, useScreenConfig, useTeamCreatorOptions } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { BatchOperationsToolbar } from './_components/BatchOperationsToolbar';
import { Breadcrumb } from './_components/Breadcrumb';
import { DragPreview } from './_components/DragPreview';
import { FileGridItem } from './_components/FileGridItem';
import { FileListItem } from './_components/FileListItem';
import { FileToolbar } from './_components/FileToolbar';
import { MoveDialog } from './_components/MoveDialog';
import { SelectionBox } from './_components/SelectionBox';
import { SortDropdown } from './_components/SortDropdown';
import { ViewModeToggle } from './_components/ViewModeToggle';
import { MAX_SELECT_ITEMS } from './_constants';
import { useDragDrop } from './_hooks/use-drag-drop';
import { useFileFilters } from './_hooks/use-file-filters';
import { useFileNavigation } from './_hooks/use-file-navigation';
import { useFileOperations } from './_hooks/use-file-operations';
import { useFileSelection } from './_hooks/use-file-selection';
import { findItemById } from './_utils/drag-utils';
import type { FileItem, OrderBy, SortBy } from './types';

const VIEW_MODE_KEY = 'VIDU_FOLDER_VIEW_MODE';
const SORT_BY_KEY = 'VIDU_FOLDER_SORT_BY';
const ORDER_KEY = 'VIDU_FOLDER_ORDER';
type ViewMode = 'grid' | 'list';

const ITEMS_PER_ROW = {
  sm: 1,
  lg: 3,
  qhd: 5,
  'qhd-plus': 6,
  default: 4,
  placeholder: 4,
};

export const FileExplorer: FC<{
  urlFolderId?: string;
  creationId?: string;
}> = ({ urlFolderId, creationId }) => {
  const t = useTranslations();
  const { supportsHover } = useDevice();
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [itemsToMove, setItemsToMove] = useState<FileItem[] | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useLocalStorageState<SortBy>(SORT_BY_KEY, {
    defaultValue: 'last_open_at',
    listenStorageChange: true,
  });
  const [order, setOrder] = useLocalStorageState<OrderBy>(ORDER_KEY, {
    defaultValue: 'newest',
    listenStorageChange: true,
  });
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(VIEW_MODE_KEY, {
    defaultValue: 'grid',
    listenStorageChange: true,
  });
  const [hydrated, setHydrated] = useState(false);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);
  const hasStartedLoadingRef = useRef(false);
  const [floatingContainer, setFloatingContainer] = useState<HTMLElement | null>(null);
  const { teamSpaceInfo, isTeamSpace } = useStore((state) => ({
    teamSpaceInfo: state.teamSpaceInfo,
    isTeamSpace: state.isTeamSpace,
  }));
  const { creatorFilterOptions } = useTeamCreatorOptions(teamSpaceInfo?.space_id);

  const showCreatorFilter = useMemo(() => {
    if (!isTeamSpace || !teamSpaceInfo) return false;
    const mode = teamSpaceInfo.asset_isolation_mode;
    if (mode === 'full_isolated') return false;
    if (mode === 'member_isolated_admin_all') return teamSpaceInfo.role === 'admin';
    return true;
  }, [isTeamSpace, teamSpaceInfo]);

  const scrollableId = useId();

  // 获取悬浮按钮容器
  useEffect(() => {
    const container = document.getElementById('floating-buttons-container');
    setFloatingContainer(container);
  }, []);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const effectiveViewMode: ViewMode = hydrated ? (viewMode ?? 'grid') : 'grid';

  const isGridView = effectiveViewMode === 'grid';
  const isListView = effectiveViewMode === 'list';

  // 文件过滤器 hook
  const { filters, updateFilters, hasActiveFilters } = useFileFilters();

  // 根据屏幕尺寸确定每行项目数，与CSS grid类保持一致
  const itemsPerRow = useScreenConfig<number>(ITEMS_PER_ROW);

  // 文件导航 hook
  const {
    files,
    currentFiles,
    breadcrumbs,
    navigateToFolder,
    handleFileOpen,
    updateFiles: rawUpdateFiles,
    isLoading,
    hasMore,
    loadMore,
    error,
    currentFolderId,
    rootFolder,
    isOpenedFile,
    hasLoadedInitial,
    openCreationDetailById,
  } = useFileNavigation({
    urlFolderId,
    search,
    sortBy,
    order,
    filters,
    itemsPerRow,
  });

  useEffect(() => {
    if (isFirstLoadComplete) return;

    if (isLoading) {
      hasStartedLoadingRef.current = true;
      return;
    }

    if (hasStartedLoadingRef.current || currentFiles.length > 0 || !!error) {
      setIsFirstLoadComplete(true);
    }
  }, [isLoading, currentFiles.length, error, isFirstLoadComplete]);

  // 文件选择 hook
  const {
    selectedItems,
    containerRef,
    selectionBox,
    isSelecting,
    handleItemClick,
    handleSelectItem,
    handleSelectAll,
    setSelectedItems,
  } = useFileSelection({
    files,
    currentFolderId,
    disabledSelectionBox: isOpenedFile || isListView || !supportsHover, // 不支持 hover 的设备（手机或平板）通常也没有滚动，拉框操作可能会跟往下滚动页面的操作冲突
  });

  // 直接根据 url 中的 creationId 跳转到 creation 详情页（用于在新标签页打开 extend 任务的源任务详情等）
  const handledCreationIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!creationId || !hasLoadedInitial || handledCreationIdRef.current === creationId) return;
    handledCreationIdRef.current = creationId;
    openCreationDetailById(creationId);
  }, [creationId, hasLoadedInitial, openCreationDetailById]);

  // 包装原始更新方法，在文件更新后，取消选中已不存在的
  const updateFiles = useCallback(
    (updater: (files: FileItem[]) => FileItem[]) => {
      rawUpdateFiles((prevFiles) => {
        const nextFiles = updater(prevFiles);
        const nextIds = new Set(nextFiles.map((f) => f.id));
        setSelectedItems((prevSelected) => prevSelected.filter((id) => nextIds.has(id)));
        return nextFiles;
      });
    },
    [rawUpdateFiles],
  );

  // 获取全选三态：true / false / 'indeterminate'
  const selectAllState = useMemo(() => {
    const selectedCount = selectedItems.length;
    const totalCount = files.length;

    if (selectedCount === 0 || totalCount === 0) {
      return false;
    }

    if (selectedCount === totalCount) {
      return true;
    }

    return 'indeterminate' as const;
  }, [files.length, selectedItems.length]);

  const handleCheckedChange = useCallback(
    () => handleSelectAll(selectAllState !== true),
    [handleSelectAll, selectAllState],
  );

  // 拖拽 hook
  const { activeId, sensors, handleDragStart, handleDragEnd, movingFiles, movingItemIds } = useDragDrop({
    files,
    selectedItems,
    onSelectionChange: setSelectedItems,
    onMoveSuccess: (movedItemIds: string[]) => {
      // 使用新的状态管理机制更新本地状态
      handleMoveComplete(movedItemIds);
    },
  });
  const selectedItemIdSet = useMemo(() => new Set(selectedItems), [selectedItems]);
  const movingItemIdSet = useMemo(() => new Set(movingItemIds), [movingItemIds]);
  const isAnyDragging = activeId !== null;

  // 文件操作 hook
  const {
    isLoading: isOperationLoading,
    handleOperation,
    handleMoveComplete,
  } = useFileOperations({
    onMoveDialogOpen: (items) => {
      setItemsToMove(items);
      setMoveDialogOpen(true);
    },
    onFilesUpdate: updateFiles,
  });

  // 虚拟化器配置
  const virtualizer = useVirtualizer({
    count: currentFiles.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => (isGridView ? 240 : 50), // 网格模式高度更大
    overscan: 5,
    // 添加调试选项以防止测量循环
    measureElement: typeof window !== 'undefined' && containerRef.current ? undefined : () => (isGridView ? 240 : 50),
  });

  // 公共的虚拟化器 ref 处理函数
  const safeVirtualizerRef = useCallback(
    (node: HTMLElement | null) => {
      // 添加安全检查避免循环调用
      if (node && typeof virtualizer.measureElement === 'function') {
        try {
          virtualizer.measureElement(node);
        } catch (error) {
          console.warn('Virtualizer measureElement error:', error);
        }
      }
    },
    [virtualizer.measureElement],
  );

  // 处理文件点击
  const handleFileItemClick = (itemId: string, event?: React.MouseEvent) => {
    const result = handleItemClick(itemId, event);
    if (result?.type === 'open') {
      handleFileOpen(result.item);
    }
  };

  // 处理确认移动
  const handleConfirmMove = (successIds: string[]) => {
    if (itemsToMove) {
      // 调用移动完成处理，更新本地状态
      handleMoveComplete(successIds);
      setItemsToMove(null);
      setMoveDialogOpen(false);
      // 清除选择
      setSelectedItems([]);
    }
  };

  const renderGridView = () => {
    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        className="absolute top-0 left-0 w-full px-8 lg:px-6 2xl:px-16"
        style={{
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => {
          const startIndex = virtualRow.index * itemsPerRow;
          const endIndex = Math.min(startIndex + itemsPerRow, currentFiles.length);
          const rowItems = currentFiles.slice(startIndex, endIndex);

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={safeVirtualizerRef}
              className={cn('grid gap-3', rowItems.length > 0 ? 'pb-3' : '')}
              style={{
                gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))`,
              }}
            >
              {rowItems.map((item: FileItem) => {
                return (
                  <FileGridItem
                    key={item.id}
                    item={item}
                    isSelected={selectedItemIdSet.has(item.id)}
                    isDraggingAny={isAnyDragging}
                    moving={Boolean(movingFiles && movingItemIdSet.has(item.id))}
                    selectedItems={selectedItems}
                    files={files}
                    isOperationLoading={isOperationLoading}
                    onFileClick={handleFileItemClick}
                    onSelectItem={handleSelectItem}
                    onOperation={handleOperation}
                    onFilesUpdate={updateFiles}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderListView = () => {
    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        className="absolute top-0 left-0 w-full px-8 lg:px-6 2xl:px-16"
        style={{
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => {
          const item = currentFiles[virtualRow.index];
          if (!item) return null;

          const isSelected = selectedItemIdSet.has(item.id);

          return (
            <div key={virtualRow.key} data-index={virtualRow.index} ref={safeVirtualizerRef}>
              <FileListItem
                item={item}
                isTeamSpace={isTeamSpace}
                isSelected={isSelected}
                isDraggingAny={isAnyDragging}
                moving={Boolean(movingFiles && movingItemIdSet.has(item.id))}
                selectedItems={selectedItems}
                files={files}
                isOperationLoading={isOperationLoading}
                onFileClick={handleFileItemClick}
                onSelectItem={handleSelectItem}
                onOperation={handleOperation}
                onFilesUpdate={updateFiles}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // 获取当前拖拽的项目
  const activeDragItem = activeId ? findItemById(files, activeId) : null;

  const handleSearchCreations = (searchValue: string) => {
    setSearch(searchValue);
  };

  // 渲染 ToTop 组件，只有在文件列表不为空时才显示
  const renderToTop = () => {
    if (!currentFiles.length || !floatingContainer) return null;

    return createPortal(<ToTop scrollDiv={containerRef.current} />, floatingContainer);
  };

  const renderEmpty = () => {
    if (!isFirstLoadComplete) {
      return <GlobalLoader />;
    }

    // 加载中
    if (isLoading) {
      return <GlobalLoader />;
    }

    // 空状态
    const isFilterOrSearch = search || hasActiveFilters;
    const Icon = isFilterOrSearch ? ProductLineEmptyFolder : ProductLineEmpty;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <Icon className="text-5xl text-system-white24" />
        <div className="text-sm text-system-white64">
          {isFilterOrSearch ? t('creations.warning.noFilterResult') : t('creations.table.allEmpty')}
        </div>
      </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      <div className="-mx-6 lg:-mx-4 relative flex h-full w-[calc(100%+48px)] flex-col lg:w-[calc(100%+32px)] 2xl:mx-auto 2xl:max-w-[2336px]">
        {selectedItems.length === 0 ? (
          <FileToolbar
            filters={filters}
            onFiltersChange={updateFilters}
            isTeamSpace={showCreatorFilter}
            creatorFilterOptions={showCreatorFilter ? creatorFilterOptions : []}
            defaultSearchValue={search}
            onSearch={handleSearchCreations}
            currentDepth={breadcrumbs.length - 1} // root 不算
            currentFolderId={currentFolderId}
            onFilesUpdate={updateFiles}
            className="sticky top-0 z-10 bg-[#0A0D11] px-8 pt-2 lg:px-6 2xl:px-16"
          />
        ) : (
          <BatchOperationsToolbar
            selectedIds={selectedItems}
            files={currentFiles}
            isLoading={isOperationLoading}
            onOperation={handleOperation}
            clearSelectedItemIds={() => setSelectedItems([])}
            isInSearch={!!search}
            className="sticky top-0 z-10 bg-[#0A0D11] px-8 pt-2 lg:px-6 2xl:px-16"
          />
        )}
        <div ref={containerRef} id={scrollableId} className="relative flex-1 overflow-y-auto">
          <div className={cn('sticky top-0 z-10 bg-[#0A0D11] px-8 lg:px-6 2xl:px-16', isGridView && 'pb-4')}>
            {/* 面包屑导航 & 排序 & 视图模式切换 */}
            <div className="ml-2 flex items-center justify-between py-3">
              {search ? (
                <span className="flex max-w-[70%] whitespace-nowrap text-sm">
                  {t.rich('creations.search.resultsAbout', {
                    keyword: () => (
                      <CustomTooltip
                        content={search}
                        showOnlyOnOverflow
                        maxLines={1}
                        contentClassName="overflow-y-auto max-h-[400px] [word-break:break-word]"
                      >
                        <span>{search}</span>
                      </CustomTooltip>
                    ),
                  })}
                </span>
              ) : (
                <Breadcrumb breadcrumbs={breadcrumbs} onNavigate={navigateToFolder} />
              )}

              {/* 排序 & 视图模式切换 */}
              <div className="flex items-center gap-4" data-no-selection>
                <SortDropdown sortBy={sortBy} order={order} onSortByChange={setSortBy} onOrderChange={setOrder} />

                {/* View Mode Toggle */}
                <ViewModeToggle viewMode={effectiveViewMode} onViewModeChange={setViewMode} />
              </div>
            </div>

            {/* 多选 */}
            <div
              className="ml-2 flex w-fit cursor-pointer select-none items-center gap-2"
              data-no-selection
              onClick={() => {
                handleCheckedChange();
              }}
            >
              <CheckboxFill checked={selectAllState} onCheckedChange={handleCheckedChange} className="text-base" />
              <span className="text-system-white64 text-xs">
                {selectedItems.length > 0 ? (
                  <span className="text-system-white64 text-xs">
                    {selectedItems.length <= 1
                      ? t('creations.batchOperation.selectedSingle', { count: selectedItems.length })
                      : t('creations.batchOperation.selected', { count: selectedItems.length })}
                  </span>
                ) : (
                  <span className="text-system-white64 text-xs">
                    {currentFiles.length <= 1
                      ? t('creations.batchOperation.selectAllSingle', {
                          count: Math.min(currentFiles.length, MAX_SELECT_ITEMS),
                        })
                      : t('creations.batchOperation.selectAll', {
                          count: Math.min(currentFiles.length, MAX_SELECT_ITEMS),
                        })}
                  </span>
                )}
              </span>
            </div>

            {/* Table Header */}
            {isListView && (
              <div
                className={cn(
                  'sticky mt-2.5 grid rounded-4 bg-system-white10 py-3 pl-2 font-semibold text-system-white text-xs sm:grid-cols-[32px_1fr_64px_56px]',
                  isTeamSpace
                    ? 'grid-cols-[32px_1fr_180px_128px_128px_120px] lg:grid-cols-[32px_1fr_148px_106px_106px_98px]'
                    : 'grid-cols-[32px_1fr_128px_128px_120px] lg:grid-cols-[32px_1fr_106px_106px_98px]',
                )}
              >
                <div /> {/* Checkbox column */}
                <div>{t('creations.folders.name')}</div>
                {isTeamSpace && <div className="sm:hidden">{t('creations.folders.creator')}</div>}
                <div>{t('creations.folders.size')}</div>
                <div>{t('creations.folders.type')}</div>
                <div className="sm:hidden">{t('creations.folders.date')}</div>
              </div>
            )}
          </div>

          {/* File Browser Content */}
          {error && (
            <div className="mx-6 mb-4 rounded-8 border border-red-500/30 bg-red-900/20 p-4">
              <div className="text-red-400 text-sm">Error loading files: {error}</div>
            </div>
          )}

          {!currentFiles.length ? (
            renderEmpty()
          ) : (
            <InfiniteScroll
              dataLength={currentFiles.length}
              next={loadMore}
              hasMore={hasMore && !isLoading}
              loader={null}
              endMessage={
                !hasMore ? (
                  <div className="flex justify-center py-4">
                    <div className="text-sm text-system-white64">{t('task.allLoaded')}</div>
                  </div>
                ) : null
              }
              scrollableTarget={scrollableId}
              scrollThreshold={0.8}
            >
              <div
                className="relative select-none"
                style={{
                  height: virtualizer.getTotalSize(),
                }}
              >
                {isGridView ? renderGridView() : renderListView()}
              </div>
              {isLoading && <BaseLineLoading2 className="mx-auto my-4 animate-spin" />}
            </InfiniteScroll>
          )}
        </div>
        {/* 选框位置相对于 containerRef 计算，但是不滚动，所以不能直接放在 containerRef 里 */}
        <div className="absolute top-12 left-0 w-full">
          <SelectionBox selectionBox={selectionBox} isSelecting={isSelecting} />
        </div>
      </div>

      <MoveDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        selectedItems={itemsToMove || []}
        onConfirm={handleConfirmMove}
        rootFolder={rootFolder}
        currentFolderId={currentFolderId}
        onFilesUpdate={updateFiles}
      />

      <DragOverlay modifiers={[snapCenterToCursor]}>
        {activeDragItem ? <DragPreview activeItem={activeDragItem} selectedItems={selectedItems} /> : null}
      </DragOverlay>
      {renderToTop()}
    </DndContext>
  );
};
